import { day } from './factory'
import { localCatalog } from '../../data/catalog'
import { pricedLines, shippingFor } from '../pricing'
import { shiftDate, today } from '../../core/format/dates'
import type { CustomerRow, OrderRow } from '../db'
import type { OrderEvent } from '../../core/types/adminRequests'
import type { Address, PaymentMethod } from '../../core/types/requests'

/**
 * DEMO — ±60 betaalde webshoporders over de laatste vier maanden, met een paar terugbetalingen
 * en een paar mislukte betalingen, zodat Bestellingen en Omzet echt iets tonen. Vast "toeval"
 * (een eigen teller), zodat de demo bij elke opbouw hetzelfde is; de datums schuiven mee met vandaag.
 */

const COUNT = 64
const DAYS_BACK = 120

const guests: Address[] = [
  { firstName: 'Iris', lastName: 'Smit', street: 'Lange Nieuwstraat', houseNumber: '12', postcode: '3512 PH', city: 'Utrecht', country: 'NL' },
  { firstName: 'Femke', lastName: 'de Boer', street: 'Wilhelminapark', houseNumber: '3', postcode: '5041 EA', city: 'Tilburg', country: 'NL' },
  { firstName: 'Noor', lastName: 'Peeters', street: 'Meir', houseNumber: '48', postcode: '2000', city: 'Antwerpen', country: 'BE' },
  { firstName: 'Sanne', lastName: 'Visser', street: 'Grote Markt', houseNumber: '7a', postcode: '9711 LV', city: 'Groningen', country: 'NL' },
  { firstName: 'Mila', lastName: 'Hendriks', street: 'Oudegracht', houseNumber: '201', postcode: '3511 NH', city: 'Utrecht', country: 'NL' },
  { firstName: 'Roos', lastName: 'Mulder', street: 'Brink', houseNumber: '19', postcode: '7411 BT', city: 'Deventer', country: 'NL' },
]

const methods: PaymentMethod[] = ['ideal', 'ideal', 'ideal', 'ideal', 'bancontact', 'klarna', 'card']

export function seedSalesOrders(customers: CustomerRow[]): OrderRow[] {
  let state = 20261003
  const rand = () => {
    state = (state * 1664525 + 1013904223) % 4294967296
    return state / 4294967296
  }
  const pick = <T,>(list: T[]): T => list[Math.floor(rand() * list.length)]

  const products = localCatalog.products.filter((p) => p.price > 0).slice(0, 24)
  const refills = products.filter((p) => p.refill)
  const now = today()

  return Array.from({ length: COUNT }, (_, i): OrderRow => {
    // Oudste eerst; de laatste twee dagen laat de bestaande inbox-voorbeelddata zien.
    const offset = -Math.round(DAYS_BACK - (i * (DAYS_BACK - 2)) / (COUNT - 1))
    const date = day(offset)
    const at = `${date}T${String(8 + Math.floor(rand() * 13)).padStart(2, '0')}:${String(Math.floor(rand() * 60)).padStart(2, '0')}`
    const customer = rand() < 0.6 && customers.length ? pick(customers).customer : null
    const address: Address = customer ? { firstName: customer.firstName, lastName: customer.lastName, ...customer.address } : pick(guests)
    const email = customer?.email ?? `${address.firstName.toLowerCase()}@voorbeeld.nl`
    const subscribe = refills.length > 0 && rand() < 0.15

    const chosen = new Map<string, number>()
    const lineCount = 1 + Math.floor(rand() * 3)
    for (let k = 0; k < lineCount; k++) {
      const p = pick(subscribe ? refills : products)
      chosen.set(p.id, (chosen.get(p.id) ?? 0) + (rand() < 0.2 ? 2 : 1))
    }
    // Af en toe een cadeaubon erbij: geen omzet, wel ontvangen geld.
    if (!subscribe && rand() < 0.12) chosen.set(pick(localCatalog.giftCards).id, 1)

    // Zelfde prijs-, btw- en verzendregels als een echte demo-bestelling (demo/pricing.ts).
    const lines = pricedLines([...chosen].map(([productId, qty]) => ({ productId, qty, subscribe })))
    const shipping = shippingFor(lines, subscribe)
    const total = Math.round((lines.reduce((s, l) => s + l.price * l.qty, 0) + shipping) * 100) / 100

    // Een paar betalingen lukten niet; de server annuleert die na het verlopen bij Mollie.
    const failed = i % 13 === 5
    const method = pick(methods)
    const events: OrderEvent[] = []
    const after = (days: number) => shiftDate(date, days)
    const stamp = (days: number, time: string) => `${after(days)}T${time}`

    let fulfilment: OrderRow['fulfilment'] = 'nieuw'
    let tracking: OrderRow['tracking']
    if (!failed) {
      const age = -offset
      if (age >= 1) {
        fulfilment = 'ingepakt'
        events.push({ at: stamp(Math.min(1, age), '10:30'), event: 'ingepakt' })
      }
      if (age >= 2) {
        fulfilment = 'verzonden'
        tracking = { carrier: 'PostNL', code: `3SBBW${String(400000 + i * 137).padStart(7, '0')}` }
        events.push({ at: stamp(2, '16:00'), event: 'verzonden' })
      }
      if (age >= 7) {
        fulfilment = 'bezorgd'
        events.push({ at: stamp(4, '12:00'), event: 'bezorgd' })
      }
    }

    // Terugbetalingen: één volledig, twee per regel, één klein gebaar zonder regels.
    const refunds: NonNullable<OrderRow['refunds']> = []
    const refundAt = after(6) <= now ? stamp(6, '11:15') : null
    if (!failed && refundAt && [9, 21, 33, 47].includes(i)) {
      const first = lines[0]
      const amount =
        i === 9 ? Math.round(total * 100) : i === 47 ? 500 : Math.round(first.price * 100)
      refunds.push({
        id: `R-${i}`,
        requestId: `seed-refund-${i}`,
        at: refundAt,
        amountCents: amount,
        reason: i === 9 ? 'Retour binnen bedenktijd' : i === 47 ? 'Excuses voor de late levering' : 'Fles kwam beschadigd aan',
        ...(i === 47 ? {} : { lines: i === 9 ? lines.map((l) => ({ productId: l.productId, qty: l.qty })) : [{ productId: first.productId, qty: 1 }] }),
      })
      events.push({ at: refundAt, event: 'terugbetaald', note: refunds[0].reason })
    }

    return {
      id: `BB-2026-S${String(i + 1).padStart(3, '0')}`,
      requestId: `seed-order-${i}`,
      receivedAt: at,
      customerId: customer?.id,
      name: `${address.firstName} ${address.lastName}`.trim(),
      city: address.city,
      fulfilment,
      ...(tracking ? { tracking } : {}),
      request: {
        email,
        address,
        lines: lines.map((l) => ({ productId: l.productId, qty: l.qty, subscribe })),
        code: null,
        skinNote: '',
        refillReminder: false,
      },
      payment: { status: failed ? (i % 2 ? 'verlopen' : 'mislukt') : 'betaald', method },
      gift: null,
      lines,
      shipping,
      total,
      viaSubscription: subscribe,
      ...(failed ? { cancelledAt: stamp(1, '00:05') } : { paidAt: at }),
      events: failed ? [{ at: stamp(1, '00:05'), event: 'geannuleerd', note: 'Betaling niet afgerond' }] : events,
      ...(refunds.length ? { refunds } : {}),
    }
  })
}
